import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { Icon } from '@iconify/react';
import { Badge, Card, Row, Col, Form, Button, Table, Modal, Nav, Tab, Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Calendar from 'react-calendar';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import { useAuth } from '../context/AuthContext';

const DoctorDashboard = () => {
    // Layout
    const [sidebarActive, setSidebarActive] = useState(false);
    const [mobileMenu, setMobileMenu] = useState(false);

    // Routing & View
    const location = useLocation();
    const [view, setView] = useState('dashboard'); // dashboard, schedule, appointments, team, settings, mr_visits

    // Data State
    const [appointments, setAppointments] = useState([]);
    const [history, setHistory] = useState([]); // New state for history
    const [team, setTeam] = useState([]);
    const [mrVisits, setMrVisits] = useState([]); // MR Visits State
    const [loading, setLoading] = useState(true);
    const [schedule, setSchedule] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]); // New state for blocked dates
    const [apptTab, setApptTab] = useState('active'); // active, history
    const [mrTab, setMrTab] = useState('upcoming'); // upcoming, completed
    const [historySearch, setHistorySearch] = useState('');
    const [invoices, setInvoices] = useState([]);
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [invoiceDateFilter, setInvoiceDateFilter] = useState('');
    const [assistantAnalysis, setAssistantAnalysis] = useState(null);
    const [historyDateFilter, setHistoryDateFilter] = useState('');

    // Filtered History Logic (Memoized for performance and stability)
    const filteredHistory = useMemo(() => {
        return history.filter(app => {
            // Robust Name Matching
            const pName = (app.patientId && app.patientId.name) ? app.patientId.name : (app.guestName || '');
            const pPhone = (app.patientId && app.patientId.phone) ? app.patientId.phone : (app.guestPhone || '');

            const matchesName = pName.toLowerCase().includes(historySearch.toLowerCase()) ||
                pPhone.includes(historySearch);

            // Robust Date Matching (ISO String comparison)
            let isDateMatch = true;
            if (historyDateFilter && app.date) {
                try {
                    // Create date object from app.date
                    const d = new Date(app.date);
                    // Create date object from filter string (YYYY-MM-DD)
                    const f = new Date(historyDateFilter);

                    // Compare Year, Month, Day in local time to avoid timezone shifts
                    isDateMatch = d.getFullYear() === f.getFullYear() &&
                        d.getMonth() === f.getMonth() &&
                        d.getDate() === f.getDate();
                } catch (e) {
                    console.error("Date parse error", e);
                    isDateMatch = false;
                }
            }
            return matchesName && isDateMatch;
        });
    }, [history, historySearch, historyDateFilter]);

    // User Context
    const { user } = useAuth();
    const [profileForm, setProfileForm] = useState({
        name: '', phone: '', specialization: '', designation: '', experienceYears: '',
        currentPassword: '', newPassword: '', confirmNewPassword: '', profileImage: ''
    });

    // RX Modal State
    const [showRxModal, setShowRxModal] = useState(false);
    const [currentAppt, setCurrentAppt] = useState(null);
    const [rxForm, setRxForm] = useState({ medicines: [], notes: '', image: '' });
    const [rxTab, setRxTab] = useState('write'); // 'write' or 'upload'

    // My Patients Field
    const [patientSearch, setPatientSearch] = useState('');
    const [selectedPatient, setSelectedPatient] = useState(null); // { info: ..., history: [] }
    const [showPatientModal, setShowPatientModal] = useState(false);

    // Assistant Details Modal
    const [selectedAssistant, setSelectedAssistant] = useState(null);
    const [showAssistantModal, setShowAssistantModal] = useState(false);

    // Queue Control
    const [queueStatus, setQueueStatus] = useState(null); // { status, reason, resumeTime }
    const [showPauseModal, setShowPauseModal] = useState(false);
    const [pauseForm, setPauseForm] = useState({ reason: '', duration: 30 }); // Default 30 mins

    const [activeFilter, setActiveFilter] = useState('today'); // 'today' or 'all'

    const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

    const isToday = (dateString) => {
        const d = new Date(dateString);
        const today = new Date();
        return d.getDate() === today.getDate() &&
            d.getMonth() === today.getMonth() &&
            d.getFullYear() === today.getFullYear();
    };

    // --- Effects ---
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const v = params.get('view') || 'dashboard';
        setView(v);
    }, [location]);

    useEffect(() => {
        if (!user) return; // Wait for auth
        if (view === 'dashboard' || view === 'patients') { fetchAppointments(); fetchHistory(); }
        if (view === 'appointments') { fetchAppointments(); fetchHistory(); }
        if (view === 'team') fetchTeam();
        if (view === 'mr_visits') fetchMRVisits();
        if (view === 'invoices') fetchInvoices();
        if (view === 'schedule' || view === 'settings' || view === 'dashboard') fetchProfileAndSchedule();
        if (view === 'dashboard') fetchQueueStatus();
    }, [view, user]);

    // Polling for Real-time feel
    useEffect(() => {
        const interval = setInterval(() => {
            if (user && (view === 'dashboard' || view === 'appointments')) {
                // Silent Refresh
                const fetchSilent = async () => {
                    try {
                        const res = await api.get('/appointments', {
                            params: { doctorId: user._id || user.id }
                        });
                        // Filter and Sort: Active first, then by Queue Number
                        let appts = res.data.filter(a => ['Active', 'Pending', 'Upcoming'].includes(a.status));
                        appts.sort((a, b) => a.queueNumber - b.queueNumber);
                        setAppointments(appts);
                    } catch (e) { }
                }
                fetchSilent();
                fetchQueueStatus();
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [user, view]);

    // --- API Interactions ---
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setProfileForm(prev => ({ ...prev, profileImage: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const fetchAppointments = async () => {
        setLoading(true);
        try {
            if (!user) return;

            // Fetch all related to active queue
            const res = await api.get('/appointments', {
                params: { doctorId: user._id || user.id }
            });

            let appts = res.data.filter(a => ['Active', 'Pending', 'Upcoming'].includes(a.status));

            // Sort: Queue Number (Ascending)
            appts.sort((a, b) => a.queueNumber - b.queueNumber);

            setAppointments(appts);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // Retry fetching history when switching to the history tab to ensure data is fresh/loaded
    useEffect(() => {
        if (apptTab === 'history' && user) {
            fetchHistory();
        }
    }, [apptTab, user]);

    const fetchHistory = async () => {
        if (!user) return;
        try {
            const res = await api.get('/appointments', {
                params: { doctorId: user._id || user.id }
            });
            const historyData = res.data.filter(a => !['Upcoming', 'Active'].includes(a.status));
            setHistory(historyData);
        } catch (error) {
            console.error("fetchHistory Error:", error);
        }
    };

    const handleViewRx = async (appt) => {
        console.log("handleViewRx called for appointment:", appt._id);
        try {
            const res = await api.get(`/prescriptions/${appt._id}`);

            if (res.data) {
                setRxForm({
                    medicines: res.data.medicines || [],
                    notes: res.data.notes || '',
                    image: res.data.imageUrl || ''
                });
                setCurrentAppt({ ...appt, readOnly: true });
                setRxTab('write');
                setShowRxModal(true);
            } else {
                Swal.fire({
                    icon: 'info',
                    title: 'No Prescription Found',
                    text: 'Prescription not available for this appointment.',
                    confirmButtonColor: '#10b981'
                });
            }
        } catch (error) {
            // console.error("Error fetching prescription:", error);
            Swal.fire({
                icon: 'info',
                title: 'No Prescription Found',
                text: 'Prescription not available for this appointment.',
                confirmButtonColor: '#10b981'
            });
        } finally {
            // Always try to fetch analysis to show if available
            fetchAssistantAnalysis(appt._id);
        }
    };

    const handleViewMissed = (app) => {
        Swal.fire({
            icon: 'info',
            title: 'Missed Appointment Reason',
            html: `<div class="text-start">
                    <p class="mb-2 text-secondary text-sm">Reason recorded by Assistant:</p>
                    <p class="fw-medium text-dark bg-light p-3 rounded border">${app.missedReason || 'No specific reason entered.'}</p>
                   </div>`,
            confirmButtonColor: '#6366f1'
        });
    };

    // --- Actions ---
    const handleStartAppt = async (id) => {
        // Constraint: Check if any other appointment is Active
        const hasActive = appointments.some(a => a.status === 'Active' && a._id !== id);
        if (hasActive) {
            return Swal.fire('Action Blocked', 'You already have an active appointment. Please complete it first.', 'warning');
        }

        const result = await Swal.fire({
            title: 'Start Consultation?',
            text: "This will make this appointment Active.",
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10B981',
            confirmButtonText: 'Yes, Start!'
        });

        if (result.isConfirmed) {
            try {
                await api.put(`/appointments/${id}/status`, { status: 'Active' });
                Swal.fire('Started', 'Appointment is now active.', 'success');
                fetchAppointments();
            } catch (error) {
                Swal.fire('Error', 'Failed to start appointment', 'error');
            }
        }
    };

    const handleCompleteAppt = async (id) => {
        const result = await Swal.fire({
            title: 'Complete Appointment?',
            text: "This will mark the appointment as completed without a prescription.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, Complete it!'
        });

        if (result.isConfirmed) {
            try {
                await api.put(`/appointments/${id}/status`, { status: 'Completed' });
                Swal.fire('Completed!', 'Appointment has been marked as completed.', 'success');

                // Refresh both lists to update counts and move items
                fetchAppointments();
                fetchHistory();
            } catch (error) {
                Swal.fire('Error', 'Failed to update status', 'error');
            }
        }
    };

    const fetchQueueStatus = async () => {
        try {
            const res = await api.get('/queue/status', {
                params: { doctorId: user._id || user.id }
            });
            setQueueStatus(res.data);
        } catch (e) {
            console.error('Queue Status Error', e);
        }
    };

    const handlePauseRequest = async (e) => {
        e.preventDefault();
        try {
            await api.post('/queue/request', pauseForm);
            Swal.fire('Request Sent', 'Assistant has been notified to stop the queue.', 'success');
            setShowPauseModal(false);
            fetchQueueStatus();
        } catch (error) {
            Swal.fire('Error', 'Failed to request pause', 'error');
        }
    };

    const handleResumeQueue = async () => {
        try {
            await api.post('/queue/resume', {});
            Swal.fire('Resumed', 'Queue is active again.', 'success');
            fetchQueueStatus();
        } catch (error) {
            Swal.fire('Error', 'Failed to resume', 'error');
        }
    };

    const handleCompleteMR = async (id) => {
        try {
            await api.put(`/mr-visits/${id}/complete`, {});
            Swal.fire('Success', 'MR Visit Completed', 'success');
            fetchMRVisits();
        } catch (error) {
            Swal.fire('Error', 'Action failed', 'error');
        }
    };



    const fetchTeam = async () => {
        setLoading(true);
        try {
            const res = await api.get('/clinic/staff');
            setTeam(res.data.filter(u => u.role === 'Assistant'));
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMRVisits = async () => {
        setLoading(true);
        try {
            const res = await api.get('/mr-visits', {
                params: { doctorId: user._id || user.id }
            });
            setMrVisits(res.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchProfileAndSchedule = async () => {
        setLoading(true);
        try {
            const res = await api.get('/users/me');
            const u = res.data;

            // Profile Form
            setProfileForm(prev => ({
                ...prev,
                name: u.name,
                phone: u.phone || '',
                specialization: u.specialization || '',
                designation: u.designation || '',
                experienceYears: u.experienceYears || '',
                profileImage: u.profileImage || ''
            }));
            // Schedule
            const existing = u.availability || [];
            const newSched = DAYS_OF_WEEK.map(day => {
                const found = existing.find(s => s.day === day);
                return found ? found : { day, startTime: '09:00', endTime: '17:00', isAvailable: false };
            });
            setSchedule(newSched);
            setBlockedDates(u.blockedDates || []); // Update blocked dates

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchInvoices = async () => {
        setLoading(true);
        try {
            const res = await api.get('/clinic/invoices');
            setInvoices(res.data);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    // --- Actions ---
    const handleScheduleChange = (index, field, value) => {
        const updated = [...schedule];
        updated[index][field] = value;
        setSchedule(updated);
    };

    const saveSchedule = async (e) => {
        e.preventDefault();
        try {
            await api.put('/users/profile', { availability: schedule });
            Swal.fire('Success', 'Availability updated successfully', 'success');
        } catch (error) {
            Swal.fire('Error', 'Failed to update schedule', 'error');
        }
    };

    const handleTurnOffToday = async () => {
        const { isConfirmed } = await Swal.fire({
            title: 'Turn Off Today?',
            text: "This will cancel all non-active appointments for today and notify patients. You cannot undo this easily.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, Turn Off'
        });

        if (isConfirmed) {
            Swal.fire({
                title: 'Processing...',
                text: 'Cancelling appointments and updating schedule. Please wait.',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            try {
                const res = await api.post('/doctor/turn-off-today', {});

                Swal.fire({
                    icon: 'success',
                    title: 'Schedule Turned Off',
                    text: res.data.message,
                    timer: 3000
                });

                fetchAppointments(); // Refresh appts
                fetchProfileAndSchedule(); // Refresh blocked status
            } catch (error) {
                Swal.fire('Error', error.response?.data?.message || 'Failed to turn off schedule', 'error');
            }
        }
    };


    const saveProfile = async (e) => {
        e.preventDefault();
        if (profileForm.newPassword && profileForm.newPassword !== profileForm.confirmNewPassword) {
            return Swal.fire('Error', 'Passwords do not match', 'error');
        }
        try {
            const payload = { ...profileForm };
            delete payload.confirmNewPassword; // Cleanup

            await api.put('/users/profile', payload);
            Swal.fire('Success', 'Profile updated successfully', 'success');
            setProfileForm(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmNewPassword: '' }));
        } catch (error) {
            Swal.fire('Error', error.response?.data?.message || 'Update failed', 'error');
        }
    };

    const handleEmailInvoice = async (id) => {
        const { value: email } = await Swal.fire({
            title: 'Email Invoice',
            input: 'email',
            inputLabel: 'Enter recipient email address',
            inputPlaceholder: 'patient@example.com',
            showCancelButton: true,
            confirmButtonText: 'Send Email',
            showLoaderOnConfirm: true,
            preConfirm: async (email) => {
                try {
                    await api.post(`/clinic/invoices/${id}/email`, { targetEmail: email });
                    Swal.fire('Sent!', 'Invoice has been emailed.', 'success');
                } catch (error) {
                    Swal.showValidationMessage(`Request failed: ${error.response?.data?.message || 'Error'}`);
                }
            },
            allowOutsideClick: () => !Swal.isLoading()
        });
    };

    // --- Helper Functions ---
    const handleMeetingLink = async (appt) => {
        const { value: url } = await Swal.fire({
            title: 'Setup Online Meeting',
            input: 'url',
            inputLabel: 'Paste Zoom/Meet URL',
            inputValue: appt.meetingLink || '',
            showCancelButton: true
        });

        if (url) {
            try {
                await api.put(`/appointments/${appt._id}/status`, { meetingLink: url });
                Swal.fire('Saved', 'Meeting Link updated.', 'success');
                // Email is sent by backend
                fetchAppointments(); // Refresh
            } catch (err) {
                Swal.fire('Error', 'Failed to save link', 'error');
            }
        }
    };

    // --- Patient Management ---
    const getUniquePatients = () => {
        const all = [...appointments, ...history];
        const unique = new Map();

        all.forEach(app => {
            const id = app.patientId ? app.patientId._id : `guest-${app.guestPhone}`;

            // Filter out doctors (if role populated) and self (if testing with own account)
            if (app.patientId) {
                if (app.patientId.role === 'Doctor') return;
                if (user && app.patientId._id === user._id) return;
            }

            if (!unique.has(id)) {
                unique.set(id, {
                    id,
                    name: app.patientId?.name || app.guestName,
                    phone: app.patientId?.phone || app.guestPhone,
                    email: app.patientId?.email || app.guestEmail || 'Not Provided',
                    profileImage: app.patientId?.profileImage,
                    isGuest: !app.patientId,
                    lastVisit: app.date
                });
            } else {
                // Update last visit if this app is newer
                const existing = unique.get(id);
                if (new Date(app.date) > new Date(existing.lastVisit)) {
                    existing.lastVisit = app.date;
                }
            }
        });
        return Array.from(unique.values());
    };

    const handlePatientClick = (patient) => {
        const all = [...appointments, ...history];
        // Filter appointments for this patient
        const patientHistory = all.filter(app => {
            const pId = app.patientId ? app.patientId._id : `guest-${app.guestPhone}`;
            return pId === patient.id;
        }).sort((a, b) => new Date(b.date) - new Date(a.date)); // Newest first

        setSelectedPatient({
            info: patient,
            history: patientHistory
        });
        setShowPatientModal(true);
    };

    const openRxModal = (appt) => {
        setCurrentAppt(appt);
        setRxForm({ medicines: [], notes: '', image: '' });
        setRxTab('write');
        setAssistantAnalysis(null);
        fetchAssistantAnalysis(appt._id);
        setShowRxModal(true);
    };

    const fetchAssistantAnalysis = async (apptId) => {
        try {
            const res = await api.get(`/assistant/analysis/${apptId}`);
            setAssistantAnalysis(res.data);
            if (res.data) {
                // If opening specifically for analysis, we might want to switch tabs, but let's default to write.
            }
        } catch (error) {
            console.error("Error fetching analysis:", error);
            setAssistantAnalysis(null);
        }
    };

    const handleViewAnalysis = async (appt) => {
        try {
            const res = await api.get(`/assistant/analysis/${appt._id}`);

            if (res.data) {
                setAssistantAnalysis(res.data);
                setCurrentAppt(appt);
                setShowRxModal(true);
                // We'll use a timeout to ensure modal render before switching tab, or just control it via state if possible
                // For now, let's just rely on the user clicking the Assistant tab or default to it? 
                // Let's force the Logic in the modal render to default to 'assistant' if we have analysis and mode is 'view'
                // But for now, just opening the Rx modal is enough as the tab is visible.
            } else {
                Swal.fire({
                    icon: 'info',
                    title: 'No Analysis Found',
                    text: 'The assistant has not uploaded any analysis for this appointment yet.',
                    confirmButtonColor: '#10b981'
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'info',
                title: 'No Analysis Found',
                text: 'The assistant has not uploaded any analysis for this appointment yet.',
                confirmButtonColor: '#10b981'
            });
        }
    };

    const openAssistantModal = (assistant) => {
        setSelectedAssistant(assistant);
        setShowAssistantModal(true);
    };

    const submitPrescription = async () => {
        try {
            const token = localStorage.getItem('token');
            const payload = {
                appointmentId: currentAppt._id,
                patientId: currentAppt.patientId?._id,
                medicines: rxForm.medicines,
                notes: rxForm.notes,
                imageUrl: rxForm.image
            };

            await api.post('/prescriptions', payload);

            Swal.fire('Success', 'Prescription sent & Appointment Completed', 'success');
            setShowRxModal(false);
            fetchAppointments();
            fetchHistory();
        } catch (err) {
            Swal.fire('Error', 'Failed to save prescription', 'error');
        }
    };

    const addMedicine = () => {
        setRxForm(prev => ({ ...prev, medicines: [...prev.medicines, { name: '', dosage: '', frequency: '', duration: '' }] }));
    };
    const updateMedicine = (idx, field, val) => {
        const updated = [...rxForm.medicines];
        updated[idx][field] = val;
        setRxForm(prev => ({ ...prev, medicines: updated }));
    };
    const removeMedicine = (idx) => {
        setRxForm(prev => ({ ...prev, medicines: prev.medicines.filter((_, i) => i !== idx) }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setRxForm(prev => ({ ...prev, image: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    // Helper to safely view images
    const handleViewImage = (url) => {
        if (!url) return;
        const win = window.open();
        if (win) {
            win.document.write(`<img src="${url}" style="width:100%; height:auto;" />`);
            win.document.title = "View File";
        }
    };

    // Toggle Layout
    const toggleSidebar = () => setSidebarActive(!sidebarActive);
    const mobileMenuControl = () => setMobileMenu(!mobileMenu);
    const closeSidebar = () => setMobileMenu(false);

    return (
        <section className={`${mobileMenu ? "overlay active" : "overlay"} transparent-scrollbar`}>
            <Sidebar isSidebarActive={sidebarActive} mobileMenu={mobileMenu} closeSidebar={closeSidebar} />
            <main className={`${sidebarActive ? "dashboard-main active" : "dashboard-main"} transparent-scrollbar`}>
                <Header toggleSidebar={toggleSidebar} mobileMenuControl={mobileMenuControl} />

                <div className="dashboard-main-body transparent-scrollbar">
                    {/* Header */}
                    <div className="d-flex align-items-center justify-content-between mb-24">
                        <div>
                            <h6 className="fw-semibold mb-1">Doctor Console</h6>
                            <p className="text-secondary-light mb-0">Manage your practice and patients</p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant={blockedDates.some(d => new Date(d).toDateString() === new Date().toDateString()) ? "secondary" : "danger"}
                                className="d-flex align-items-center gap-2"
                                onClick={handleTurnOffToday}
                                disabled={blockedDates.some(d => new Date(d).toDateString() === new Date().toDateString())}
                            >
                                <Icon icon="solar:power-bold" />
                                {blockedDates.some(d => new Date(d).toDateString() === new Date().toDateString()) ? "Off for Today" : "Turn Off Today"}
                            </Button>
                            {queueStatus?.status === 'Paused' && (
                                <Button variant="warning" className="d-flex align-items-center gap-2" onClick={handleResumeQueue}>
                                    <Icon icon="solar:play-bold" /> Resume Queue
                                </Button>
                            )}
                            {queueStatus?.status === 'Active' && view === 'dashboard' && (
                                <Button
                                    variant="outline-danger"
                                    className="d-flex align-items-center gap-2"
                                    onClick={() => {
                                        const isOffToday = blockedDates.some(d => new Date(d).toDateString() === new Date().toDateString());
                                        if (isOffToday) {
                                            Swal.fire({
                                                icon: 'info',
                                                title: 'Queue Already Off',
                                                text: 'All schedules are already turned off for today.',
                                                confirmButtonColor: '#10b981'
                                            });
                                        } else {
                                            setShowPauseModal(true);
                                        }
                                    }}
                                >
                                    <Icon icon="solar:pause-bold" /> Pause Queue
                                </Button>
                            )}
                            <span className="badge bg-primary-50 text-primary-600 px-3 py-2 rounded-pill fw-medium d-flex align-items-center gap-2">
                                <Icon icon="solar:stethoscope-bold" /> {user?.specialization || 'General'}
                            </span>
                        </div>
                    </div>

                    {queueStatus?.status === 'PauseRequested' && (
                        <div className="alert alert-warning d-flex align-items-center gap-3 mb-4">
                            <Icon icon="solar:clock-circle-bold" className="text-2xl" />
                            <div>
                                <strong>Request Pending:</strong> You have requested to pause the queue for {queueStatus.pauseDuration} mins. Waiting for assistant confirmation.
                            </div>
                        </div>
                    )}

                    {queueStatus?.status === 'Paused' && (
                        <div className="alert alert-danger d-flex align-items-center gap-3 mb-4">
                            <Icon icon="solar:forbidden-circle-bold" className="text-2xl" />
                            <div>
                                <strong>Queue Paused:</strong> Accepting no new patients until {new Date(queueStatus.resumeTime).toLocaleTimeString()}.
                                <br />Reason: {queueStatus.reason}
                            </div>
                        </div>
                    )}

                    {/* CONTENT AREA */}

                    {/* 1. DASHBOARD OVERVIEW */}
                    {view === 'dashboard' && (
                        <Row className="gy-4">
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm radius-12" style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
                                    <Card.Header className="bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center flex-shrink-0">
                                        <h6 className="mb-0 fw-bold">Active & Upcoming Appointments</h6>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant={activeFilter === 'today' ? 'primary' : 'outline-primary'}
                                                size="sm"
                                                className="rounded-pill px-3"
                                                onClick={() => setActiveFilter('today')}
                                            >
                                                Today
                                            </Button>
                                            <Button
                                                variant={activeFilter === 'all' ? 'primary' : 'outline-primary'}
                                                size="sm"
                                                className="rounded-pill px-3"
                                                onClick={() => setActiveFilter('all')}
                                            >
                                                All
                                            </Button>
                                        </div>
                                    </Card.Header>
                                    <Card.Body className="p-0 flex-grow-1 overflow-hidden">
                                        {loading ? <div className="p-4 text-center">Loading...</div> :
                                            (() => {
                                                const filteredAppts = appointments.filter(a => activeFilter === 'today' ? isToday(a.date) : true);

                                                if (filteredAppts.length === 0) return (
                                                    <div className="p-5 text-center text-secondary">
                                                        <Icon icon="solar:cup-hot-bold-duotone" className="text-4xl mb-2 opacity-50" />
                                                        <p>No active or upcoming appointments {activeFilter === 'today' ? 'for today' : ''}.</p>
                                                    </div>
                                                );

                                                return (
                                                    <div className="table-responsive h-100 transparent-scrollbar" style={{ overflowY: 'auto' }}>
                                                        <Table hover className="mb-0 align-middle">
                                                            <thead className="bg-light sticky-top">
                                                                <tr>
                                                                    <th className="ps-4">Q-ID</th>
                                                                    <th>Date & Time</th>
                                                                    <th>Patient</th>
                                                                    <th>Status</th>
                                                                    <th>Action</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {filteredAppts.map(app => (
                                                                    <tr key={app._id} onClick={() => setView('appointments')} style={{ cursor: 'pointer' }}>
                                                                        <td className="ps-4 fw-medium text-primary">
                                                                            {app.isEmergency && <Icon icon="solar:danger-triangle-bold" className="text-danger me-1" />}
                                                                            #{app.queueNumber}
                                                                        </td>
                                                                        <td>
                                                                            <div className="fw-medium">{app.timeSlot}</div>
                                                                            <div className="text-xs text-secondary">{new Date(app.date).toLocaleDateString()}</div>
                                                                        </td>
                                                                        <td>
                                                                            <div className="fw-medium">{app.patientId?.name || app.guestName}</div>
                                                                            <div className="text-xs text-secondary">{app.type}</div>
                                                                        </td>
                                                                        <td>
                                                                            <Badge bg={app.status === 'Active' ? 'success' : 'light'} text={app.status === 'Active' ? 'white' : 'dark'}>
                                                                                {app.status}
                                                                            </Badge>
                                                                        </td>
                                                                        <td onClick={(e) => e.stopPropagation()}>
                                                                            {app.status !== 'Active' && (
                                                                                <OverlayTrigger
                                                                                    placement="top"
                                                                                    overlay={
                                                                                        <Tooltip>
                                                                                            {appointments.some(a => a.status === 'Active')
                                                                                                ? "Another appointment is currently in progress. Please complete it first."
                                                                                                : !isToday(app.date)
                                                                                                    ? "Consultations can only be initiated on the scheduled appointment date."
                                                                                                    : "Start Consultation"}
                                                                                        </Tooltip>
                                                                                    }
                                                                                >
                                                                                    <span className="d-inline-block">
                                                                                        <Button
                                                                                            variant={appointments.some(a => a.status === 'Active') ? "outline-secondary" : "outline-primary"}
                                                                                            size="sm"
                                                                                            className="py-1 px-2"
                                                                                            style={{ pointerEvents: (appointments.some(a => a.status === 'Active') || !isToday(app.date)) ? 'none' : 'auto' }}
                                                                                            disabled={appointments.some(a => a.status === 'Active') || !isToday(app.date)}
                                                                                            onClick={() => handleStartAppt(app._id)}
                                                                                        >
                                                                                            Start
                                                                                        </Button>
                                                                                    </span>
                                                                                </OverlayTrigger>
                                                                            )}
                                                                            {app.status === 'Active' && (
                                                                                <Button
                                                                                    variant="success"
                                                                                    size="sm"
                                                                                    className="py-1 px-2"
                                                                                    disabled
                                                                                >
                                                                                    Active
                                                                                </Button>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                );
                                            })()
                                        }
                                    </Card.Body>
                                </Card>
                            </Col >
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm radius-12" style={{ height: '80vh', overflowY: 'auto' }}>
                                    <Card.Body className="transparent-scrollbar">
                                        <h6 className="mb-3">Quick Stats</h6>
                                        <div className="d-flex align-items-center justify-content-between p-3 bg-primary-50 rounded-3 mb-3">
                                            <div>
                                                <small className="text-primary-600 fw-medium">Total Active</small>
                                                <h4 className="mb-0 text-primary-600">{appointments.length}</h4>
                                            </div>
                                            <Icon icon="solar:calendar-mark-bold-duotone" className="text-3xl text-primary-300" />
                                        </div>
                                        <div className="d-flex align-items-center justify-content-between p-3 bg-success-50 rounded-3">
                                            <div>
                                                <small className="text-success-600 fw-medium">Completed</small>
                                                <h4 className="mb-0 text-success-600">{history.length}</h4>
                                            </div>
                                            <Icon icon="solar:check-circle-bold-duotone" className="text-3xl text-success-300" />
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row >
                    )}

                    {/* 2. SCHEDULE MANAGER */}
                    {
                        view === 'schedule' && (
                            <Card className="border-0 shadow-sm radius-12">
                                <Card.Header className="bg-white border-bottom py-3 px-4 d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0 fw-bold">Manage Availability</h6>
                                    <Button variant="primary" onClick={saveSchedule} size="sm" className="d-flex align-items-center gap-2">
                                        <Icon icon="solar:diskette-bold" /> Save Changes
                                    </Button>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    {loading ? <div>Loading schedule...</div> :
                                        <Form>
                                            {schedule.map((slot, index) => (
                                                <Row key={slot.day} className="align-items-center mb-3 pb-3 border-bottom border-light-subtle last-0">
                                                    <Col md={3}>
                                                        <Form.Check
                                                            type="switch"
                                                            id={`switch-${slot.day}`}
                                                            label={<span className="fw-medium ms-2">{slot.day}</span>}
                                                            checked={slot.isAvailable}
                                                            onChange={(e) => handleScheduleChange(index, 'isAvailable', e.target.checked)}
                                                        />
                                                    </Col>
                                                    <Col md={4}>
                                                        <Form.Control
                                                            type="time"
                                                            value={slot.startTime}
                                                            disabled={!slot.isAvailable}
                                                            onChange={(e) => handleScheduleChange(index, 'startTime', e.target.value)}
                                                        />
                                                    </Col>
                                                    <Col md={1} className="text-center text-secondary">to</Col>
                                                    <Col md={4}>
                                                        <Form.Control
                                                            type="time"
                                                            value={slot.endTime}
                                                            disabled={!slot.isAvailable}
                                                            onChange={(e) => handleScheduleChange(index, 'endTime', e.target.value)}
                                                        />
                                                    </Col>
                                                </Row>
                                            ))}
                                        </Form>
                                    }
                                </Card.Body>
                            </Card>
                        )
                    }

                    {/* 3. APPOINTMENTS LIST */}
                    {/* 3. APPOINTMENTS (Tabs: Active vs History) */}
                    {
                        view === 'appointments' && (
                            <Card className="border-0 shadow-sm radius-12" style={{ height: '80vh', display: 'flex', flexDirection: 'column' }}>
                                <Tab.Container activeKey={apptTab} onSelect={(k) => setApptTab(k)}>
                                    <Card.Header className="bg-white border-bottom p-0 flex-shrink-0">
                                        <Nav variant="tabs" className="px-3 pt-3">
                                            <Nav.Item>
                                                <Nav.Link eventKey="active" className="fw-medium px-4">Active & Upcoming</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="history" className="fw-medium px-4">History & Completed</Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    </Card.Header>
                                    <Card.Body className="p-0 flex-grow-1 overflow-hidden">
                                        <Tab.Content className="h-100">
                                            <Tab.Pane eventKey="active" className="h-100">
                                                <div className="h-100 d-flex flex-column">
                                                    <div className="d-flex justify-content-end p-3 border-bottom bg-light-subtle flex-shrink-0">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <Button
                                                                variant={activeFilter === 'today' ? 'primary' : 'outline-primary'}
                                                                size="sm"
                                                                className="rounded-pill px-3"
                                                                onClick={() => setActiveFilter('today')}
                                                            >
                                                                Today
                                                            </Button>
                                                            <Button
                                                                variant={activeFilter === 'all' ? 'primary' : 'outline-primary'}
                                                                size="sm"
                                                                className="rounded-pill px-3"
                                                                onClick={() => setActiveFilter('all')}
                                                            >
                                                                All
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <div className="table-responsive flex-grow-1 transparent-scrollbar" style={{ overflowY: 'auto' }}>
                                                        {(() => {
                                                            const filteredAppts = appointments.filter(a => activeFilter === 'today' ? isToday(a.date) : true);

                                                            if (filteredAppts.length === 0) return (
                                                                <div className="text-center p-5">
                                                                    <Icon icon="solar:calendar-mark-bold-duotone" className="text-5xl text-secondary opacity-50 mb-3" />
                                                                    <p className="text-secondary fw-medium">No active appointments {activeFilter === 'today' ? 'for today' : ''}.</p>
                                                                </div>
                                                            );

                                                            return (
                                                                <Table hover className="mb-0 align-middle">
                                                                    <thead className="bg-light sticky-top">
                                                                        <tr>
                                                                            <th className="ps-4">Date</th>
                                                                            <th>Time</th>
                                                                            <th>Patient</th>
                                                                            <th>Type</th>
                                                                            <th>Status</th>
                                                                            <th>Actions</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {filteredAppts.map(app => (
                                                                            <tr key={app._id}>
                                                                                <td className="ps-4 text-secondary">{new Date(app.date).toLocaleDateString()}</td>
                                                                                <td className="fw-medium">{app.timeSlot}</td>
                                                                                <td>
                                                                                    <div className="fw-bold">{app.patientId?.name || app.guestName}</div>
                                                                                    <div className="text-xs text-secondary">{app.patientId?.phone || app.guestPhone}</div>
                                                                                    <div className="text-xs text-secondary">{app.patientId?.email || app.guestEmail || ''}</div>
                                                                                </td>
                                                                                <td>{app.type}</td>
                                                                                <td><Badge bg={app.status === 'Active' ? 'success' : 'warning'}>{app.status}</Badge></td>
                                                                                <td>
                                                                                    <div className="d-flex align-items-center justify-content-start gap-2">
                                                                                        {app.status !== 'Active' ? (
                                                                                            <OverlayTrigger
                                                                                                placement="top"
                                                                                                overlay={
                                                                                                    <Tooltip>
                                                                                                        {appointments.some(a => a.status === 'Active')
                                                                                                            ? "Another appointment is currently in progress. Please complete it first."
                                                                                                            : !isToday(app.date)
                                                                                                                ? "Consultations can only be initiated on the scheduled appointment date."
                                                                                                                : "Start Appointment"}
                                                                                                    </Tooltip>
                                                                                                }
                                                                                            >
                                                                                                <span className="d-inline-block">
                                                                                                    <Button
                                                                                                        variant={appointments.some(a => a.status === 'Active') ? "secondary" : "success"}
                                                                                                        size="sm"
                                                                                                        className="px-3"
                                                                                                        style={{ pointerEvents: (appointments.some(a => a.status === 'Active') || !isToday(app.date)) ? 'none' : 'auto' }}
                                                                                                        disabled={appointments.some(a => a.status === 'Active') || !isToday(app.date)}
                                                                                                        onClick={() => handleStartAppt(app._id)}
                                                                                                    >
                                                                                                        <div className="d-flex align-items-center gap-2">
                                                                                                            <Icon icon="solar:play-bold" />
                                                                                                            <span className="d-none d-md-inline">Start</span>
                                                                                                        </div>
                                                                                                    </Button>
                                                                                                </span>
                                                                                            </OverlayTrigger>
                                                                                        ) : (
                                                                                            <>
                                                                                                {app.type === 'Online' && (
                                                                                                    <Button variant="primary" size="sm" onClick={() => handleMeetingLink(app)} title="Join Meeting">
                                                                                                        <Icon icon="solar:link-circle-bold" />
                                                                                                    </Button>
                                                                                                )}
                                                                                                <Button variant="info" size="sm" className="text-white" onClick={() => openRxModal(app)} title="Prescription">
                                                                                                    <div className="d-flex align-items-center gap-1">
                                                                                                        <Icon icon="solar:clipboard-check-bold" /> Rx
                                                                                                    </div>
                                                                                                </Button>
                                                                                                <Button variant="success" size="sm" onClick={() => handleCompleteAppt(app._id)} title="Complete">
                                                                                                    <Icon icon="solar:check-circle-bold" />
                                                                                                </Button>
                                                                                            </>
                                                                                        )}
                                                                                    </div>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </Table>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="history" className="h-100">
                                                <div className="h-100 d-flex flex-column">
                                                    <div className="p-3 border-bottom bg-light-subtle">

                                                        <Row className="g-2">
                                                            <Col md={8}>
                                                                <Form.Control
                                                                    type="text"
                                                                    placeholder="Search by Patient Name or Phone..."
                                                                    size="sm"
                                                                    value={historySearch}
                                                                    onChange={(e) => setHistorySearch(e.target.value)}
                                                                />
                                                            </Col>
                                                            <Col md={4}>
                                                                <Form.Control
                                                                    type="date"
                                                                    size="sm"
                                                                    value={historyDateFilter}
                                                                    onChange={(e) => setHistoryDateFilter(e.target.value)}
                                                                />
                                                            </Col>
                                                        </Row>
                                                    </div>

                                                    <div className="table-responsive flex-grow-1 transparent-scrollbar" style={{ overflowY: 'auto' }}>
                                                        {history.length === 0 ? (
                                                            <div className="text-center p-5">
                                                                <Icon icon="solar:history-bold-duotone" className="text-5xl text-secondary opacity-50 mb-3" />
                                                                <p className="text-secondary fw-medium">No past appointments found in your record.</p>
                                                            </div>
                                                        ) : filteredHistory.length === 0 ? (
                                                            <div className="text-center p-5">
                                                                <Icon icon="solar:magnifer-zoom-out-bold-duotone" className="text-5xl text-secondary opacity-50 mb-3" />
                                                                <p className="text-secondary fw-medium">No appointments match your search/filter criteria.</p>
                                                            </div>
                                                        ) : (
                                                            <Table hover className="mb-0 align-middle">
                                                                <thead className="bg-light">
                                                                    <tr>
                                                                        <th className="ps-4">Date</th>
                                                                        <th>Time</th>
                                                                        <th>Patient</th>
                                                                        <th>Type</th>
                                                                        <th>Status</th>
                                                                        <th>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {filteredHistory.map(app => (
                                                                        <tr key={app._id}>
                                                                            <td className="ps-4 text-secondary">{new Date(app.date).toLocaleDateString()}</td>
                                                                            <td className="fw-medium">{app.timeSlot}</td>
                                                                            <td>
                                                                                <div>{app.patientId?.name || app.guestName}</div>
                                                                                <div className="text-xs text-secondary">{app.patientId?.phone || app.guestPhone}</div>
                                                                                <div className="text-xs text-secondary">{app.patientId?.email || app.guestEmail || ''}</div>
                                                                            </td>
                                                                            <td>{app.type}</td>
                                                                            <td>
                                                                                <Badge bg={app.status === 'Completed' ? 'success' : 'danger'}>{app.status}</Badge>
                                                                            </td>
                                                                            <td>
                                                                                {app.status === 'Completed' && (
                                                                                    <Button variant="outline-dark text-xs px-2 py-1" size="sm" onClick={() => handleViewRx(app)}>
                                                                                        <Icon icon="solar:eye-bold" /> View Rx
                                                                                    </Button>
                                                                                )}
                                                                                {app.status === 'Missed' && (
                                                                                    <Button variant="outline-danger text-xs px-2 py-1" size="sm" onClick={() => handleViewMissed(app)}>
                                                                                        <Icon icon="solar:info-circle-bold" /> View Reason
                                                                                    </Button>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        )}
                                                    </div>
                                                </div>
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Card.Body>
                                </Tab.Container>
                            </Card>
                        )
                    }

                    {/* 4. MY PATIENTS VIEW */}
                    {
                        view === 'patients' && (
                            <div className="card h-100 p-0 radius-12">
                                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                        <h5 className="mb-0">My Patients</h5>
                                        <span className="badge bg-primary-50 text-primary-600 border border-primary-100 fw-medium">
                                            {getUniquePatients().length} Total
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="search-box">
                                            <input
                                                type="text"
                                                className="form-control form-control-sm w-auto ps-40 h-40-px radius-8"
                                                placeholder="Search Patients..."
                                                value={patientSearch}
                                                onChange={(e) => setPatientSearch(e.target.value)}
                                                style={{ minWidth: '250px' }}
                                            />
                                            <Icon icon="lucide:search" className="position-absolute top-50 start-0 translate-middle-y ms-20 text-secondary-light text-lg" />
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body p-24">
                                    {getUniquePatients().filter(p => p.name.toLowerCase().includes(patientSearch.toLowerCase())).length === 0 ? (
                                        <div className="text-center p-5">
                                            <Icon icon="solar:users-group-rounded-bold-duotone" className="text-5xl text-secondary opacity-50 mb-3" />
                                            <p className="text-secondary fw-medium">No patients found.</p>
                                        </div>
                                    ) : (
                                        <div className="table-responsive scroll-sm">
                                            <table className="table bordered-table sm-table mb-0">
                                                <thead>
                                                    <tr>
                                                        <th>Patient Name</th>
                                                        <th>Contact Details</th>
                                                        <th>Last Visit</th>
                                                        <th>Type</th>
                                                        <th>Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {getUniquePatients()
                                                        .filter(p => p.name.toLowerCase().includes(patientSearch.toLowerCase()))
                                                        .map(patient => (
                                                            <tr key={patient.id}>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        {patient.profileImage ?
                                                                            <img src={patient.profileImage} alt="" className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden object-fit-cover" /> :
                                                                            <div className="w-40-px h-40-px bg-info-50 text-info-600 d-flex justify-content-center align-items-center rounded-circle flex-shrink-0 me-12 fw-bold text-lg">
                                                                                {patient.name.charAt(0)}
                                                                            </div>
                                                                        }
                                                                        <div className="flex-grow-1">
                                                                            <h6 className="text-md mb-0 fw-medium">{patient.name}</h6>
                                                                            <span className="text-sm text-secondary-light fw-normal">ID: {patient.id.slice(-6).toUpperCase()}</span>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex flex-column">
                                                                        <span className="text-sm text-secondary-light fw-normal mb-1">
                                                                            <Icon icon="solar:phone-calling-linear" className="me-1" /> {patient.phone}
                                                                        </span>
                                                                        <span className="text-sm text-secondary-light fw-normal">
                                                                            <Icon icon="solar:letter-linear" className="me-1" /> {patient.email}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <Icon icon="solar:calendar-linear" className="text-primary-light" />
                                                                        <span className="text-sm fw-medium text-primary-600">
                                                                            {new Date(patient.lastVisit).toLocaleDateString()}
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {patient.isGuest ?
                                                                        <span className="bg-secondary-50 text-secondary-600 px-12 py-4 radius-4 fw-medium text-sm">Guest</span> :
                                                                        <span className="bg-info-50 text-info-600 px-12 py-4 radius-4 fw-medium text-sm">Registered</span>
                                                                    }
                                                                </td>
                                                                <td>
                                                                    <button
                                                                        className="w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-flex justify-content-center align-items-center hover-bg-primary-600 hover-text-white transition-2"
                                                                        onClick={() => handlePatientClick(patient)}
                                                                        title="View History"
                                                                    >
                                                                        <Icon icon="solar:eye-bold" />
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )
                    }

                    {/* 5. TEAM VIEW */}
                    {
                        view === 'team' && (
                            <Card className="border-0 shadow-sm radius-12">
                                <Card.Header className="bg-white border-bottom py-3 px-4">
                                    <h6 className="mb-0 fw-bold">My Assistants</h6>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Row className="g-4">
                                        {loading ? <div className="p-4 text-center">Loading team...</div> :
                                            team.length === 0 ? (
                                                <div className="col-12 text-center p-5">
                                                    <Icon icon="solar:users-group-rounded-bold-duotone" className="text-6xl text-secondary opacity-50 mb-3" />
                                                    <p className="text-secondary fw-medium">No assistants linked to your clinic yet.</p>
                                                </div>
                                            ) : (
                                                team.map(member => (
                                                    <Col md={4} key={member._id}>
                                                        <div
                                                            className="d-flex align-items-center p-3 border rounded-3 bg-white shadow-sm h-100 cursor-pointer bg-hover-light transition-200"
                                                            onClick={() => openAssistantModal(member)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="w-50-px h-50-px rounded-circle bg-primary-50 d-flex justify-content-center align-items-center flex-shrink-0 me-3">
                                                                <Icon icon="solar:user-id-bold" className="text-2xl text-primary" />
                                                            </div>
                                                            <div>
                                                                <h6 className="mb-1 text-sm">{member.name}</h6>
                                                                <p className="text-xs text-secondary mb-0">{member.phone || 'No Phone'}</p>
                                                                <Badge bg="success" className="mt-1">Active</Badge>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                ))
                                            )
                                        }
                                    </Row>
                                </Card.Body>
                            </Card>
                        )
                    }

                    {/* ASSISTANT DETAIL MODAL */}
                    <Modal show={showAssistantModal} onHide={() => setShowAssistantModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Assistant Details</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {selectedAssistant && (
                                <div className="text-center">
                                    <div className="w-80-px h-80-px rounded-circle bg-primary-50 d-flex justify-content-center align-items-center mx-auto mb-3">
                                        <Icon icon="solar:user-id-bold" className="text-4xl text-primary" />
                                    </div>
                                    <h5 className="mb-1">{selectedAssistant.name}</h5>
                                    <Badge bg="info" className="mb-3">{selectedAssistant.role}</Badge>

                                    <div className="text-start bg-light p-3 rounded-3 mt-2">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-secondary">Email:</span>
                                            <span className="fw-medium">{selectedAssistant.email}</span>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span className="text-secondary">Phone:</span>
                                            <span className="fw-medium">{selectedAssistant.phone || 'N/A'}</span>
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span className="text-secondary">Joined:</span>
                                            <span className="fw-medium">{new Date(selectedAssistant.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowAssistantModal(false)}>Close</Button>
                        </Modal.Footer>
                    </Modal>

                    {/* PAUSE MODAL */}
                    <Modal show={showPauseModal} onHide={() => setShowPauseModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Pause Patient Queue</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <Form onSubmit={handlePauseRequest}>
                                <div className="alert alert-info text-sm">
                                    Requests will be sent to the assistant. Upon confirmation, waiting patients will be notified via email about the delay.
                                </div>
                                <Form.Group className="mb-3">
                                    <Form.Label>Reason for Pause</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="e.g. Emergency Surgery, Lunch Break..."
                                        required
                                        value={pauseForm.reason}
                                        onChange={e => setPauseForm({ ...pauseForm, reason: e.target.value })}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label>Duration (Minutes)</Form.Label>
                                    <Form.Select
                                        value={pauseForm.duration}
                                        onChange={e => setPauseForm({ ...pauseForm, duration: Number(e.target.value) })}
                                    >
                                        <option value="15">15 Minutes</option>
                                        <option value="30">30 Minutes</option>
                                        <option value="45">45 Minutes</option>
                                        <option value="60">1 Hour</option>
                                        <option value="90">1.5 Hours</option>
                                        <option value="120">2 Hours</option>
                                    </Form.Select>
                                </Form.Group>
                                <div className="d-flex justify-content-end gap-2">
                                    <Button variant="secondary" onClick={() => setShowPauseModal(false)}>Cancel</Button>
                                    <Button variant="danger" type="submit">Request Pause</Button>
                                </div>
                            </Form>
                        </Modal.Body>
                    </Modal>

                    {/* 5. MR VISITS VIEW */}
                    {
                        view === 'mr_visits' && (
                            <Card className="border-0 shadow-sm radius-12">
                                <Card.Header className="bg-white border-bottom py-3 px-4">
                                    <h6 className="mb-0 fw-bold">Daily Medical Representative Visits</h6>
                                </Card.Header>
                                <Tab.Container activeKey={mrTab} onSelect={(k) => setMrTab(k)}>
                                    <Card.Header className="bg-white border-bottom p-0">
                                        <Nav variant="tabs" className="px-3 pt-3">
                                            <Nav.Item>
                                                <Nav.Link eventKey="upcoming" className="fw-medium px-4">Upcoming</Nav.Link>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Nav.Link eventKey="completed" className="fw-medium px-4">Completed & History</Nav.Link>
                                            </Nav.Item>
                                        </Nav>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        <Tab.Content>
                                            <Tab.Pane eventKey="upcoming">
                                                {(() => {
                                                    const visibleVisits = mrVisits.filter(m => m.status !== 'Completed');
                                                    if (visibleVisits.length === 0) return (
                                                        <div className="text-center p-5">
                                                            <Icon icon="solar:user-id-bold-duotone" className="text-6xl text-secondary opacity-50 mb-3" />
                                                            <p className="text-secondary fw-medium">No active MR visits.</p>
                                                        </div>
                                                    );
                                                    return (
                                                        <div className="table-responsive">
                                                            <Table hover className="mb-0 align-middle">
                                                                <thead className="bg-light">
                                                                    <tr>
                                                                        <th className="ps-4">Time</th>
                                                                        <th>MR Name</th>
                                                                        <th>Company</th>
                                                                        <th>Purpose</th>
                                                                        <th>Status</th>
                                                                        <th className="text-end pe-4">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {visibleVisits.map(visit => (
                                                                        <tr key={visit._id}>
                                                                            <td className="ps-4 text-secondary">
                                                                                {new Date(visit.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                            </td>
                                                                            <td className="fw-medium">{visit.mrName}</td>
                                                                            <td>{visit.companyName}</td>
                                                                            <td className="text-truncate" style={{ maxWidth: '200px' }}>{visit.purpose || 'Not Mentioned'}</td>
                                                                            <td>
                                                                                <Badge bg="warning" className="rounded-pill px-3">
                                                                                    {visit.status || 'Waiting'}
                                                                                </Badge>
                                                                            </td>
                                                                            <td className="text-end pe-4">
                                                                                <Button variant="outline-success" size="sm" onClick={() => handleCompleteMR(visit._id)} className="d-flex align-items-center gap-2 ms-auto">
                                                                                    <Icon icon="solar:check-circle-bold" /> Complete
                                                                                </Button>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    );
                                                })()}
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="completed">
                                                {(() => {
                                                    const visibleVisits = mrVisits.filter(m => m.status === 'Completed');
                                                    if (visibleVisits.length === 0) return (
                                                        <div className="text-center p-5">
                                                            <Icon icon="solar:history-bold-duotone" className="text-6xl text-secondary opacity-50 mb-3" />
                                                            <p className="text-secondary fw-medium">No completed MR visits.</p>
                                                        </div>
                                                    );
                                                    return (
                                                        <div className="table-responsive">
                                                            <Table hover className="mb-0 align-middle">
                                                                <thead className="bg-light">
                                                                    <tr>
                                                                        <th className="ps-4">Time</th>
                                                                        <th>MR Name</th>
                                                                        <th>Company</th>
                                                                        <th>Purpose</th>
                                                                        <th>Status</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {visibleVisits.map(visit => (
                                                                        <tr key={visit._id}>
                                                                            <td className="ps-4 text-secondary">
                                                                                {new Date(visit.checkInTime).toLocaleDateString()} {new Date(visit.checkInTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                                            </td>
                                                                            <td className="fw-medium">{visit.mrName}</td>
                                                                            <td>{visit.companyName}</td>
                                                                            <td className="text-truncate" style={{ maxWidth: '200px' }}>{visit.purpose || 'Not Mentioned'}</td>
                                                                            <td>
                                                                                <Badge bg="success" className="rounded-pill px-3">Completed</Badge>
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    );
                                                })()}
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Card.Body>
                                </Tab.Container>
                            </Card>
                        )
                    }

                    {/* 6. SETTINGS / PROFILE */}
                    {
                        view === 'settings' && (
                            <Card className="border-0 shadow-sm radius-12">
                                <Card.Header className="bg-white border-bottom py-3 px-4">
                                    <h6 className="mb-0 fw-bold">Doctor Profile Settings</h6>
                                </Card.Header>
                                <Card.Body className="p-4">
                                    <Form onSubmit={saveProfile}>
                                        <div className="d-flex align-items-center gap-4 mb-4">
                                            <div className="position-relative">
                                                <div className="w-100-px h-100-px radius-circle bg-primary-50 border border-3 border-white shadow-sm overflow-hidden d-flex align-items-center justify-content-center">
                                                    {profileForm.profileImage ? (
                                                        <img src={profileForm.profileImage} alt="Profile" className="w-100 h-100 object-fit-cover" />
                                                    ) : (
                                                        <Icon icon="solar:user-bold-duotone" className="text-4xl text-primary-300" />
                                                    )}
                                                </div>
                                                <label className="position-absolute bottom-0 end-0 bg-white border shadow-sm radius-circle p-2 cursor-pointer hover-scale-sm">
                                                    <input type="file" className="d-none" accept="image/*" onChange={handleImageChange} />
                                                    <Icon icon="solar:camera-bold" className="text-primary-600 text-sm" />
                                                </label>
                                            </div>
                                            <div>
                                                <h6 className="fw-bold mb-1">{profileForm.name || 'Your Name'}</h6>
                                                <p className="text-secondary-light text-sm mb-0">Upload a professional photo for patients to identify you.</p>
                                            </div>
                                        </div>

                                        <h6 className="text-primary-600 mb-3">Professional Details</h6>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Full Name</Form.Label>
                                                    <Form.Control type="text" value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} required />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Specialization</Form.Label>
                                                    <Form.Control type="text" value={profileForm.specialization} onChange={e => setProfileForm({ ...profileForm, specialization: e.target.value })} required />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Designation</Form.Label>
                                                    <Form.Control type="text" value={profileForm.designation} onChange={e => setProfileForm({ ...profileForm, designation: e.target.value })} required />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Experience (Years)</Form.Label>
                                                    <Form.Control type="number" value={profileForm.experienceYears} onChange={e => setProfileForm({ ...profileForm, experienceYears: e.target.value })} required />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <h6 className="text-primary-600 mb-3">Account Security</h6>
                                        <Row className="mb-3">
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Phone</Form.Label>
                                                    <Form.Control type="text" value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Verify Current Password</Form.Label>
                                                    <Form.Control type="password" value={profileForm.currentPassword} onChange={e => setProfileForm({ ...profileForm, currentPassword: e.target.value })} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>New Password</Form.Label>
                                                    <Form.Control type="password" value={profileForm.newPassword} onChange={e => setProfileForm({ ...profileForm, newPassword: e.target.value })} />
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group className="mb-3">
                                                    <Form.Label>Confirm New Password</Form.Label>
                                                    <Form.Control type="password" value={profileForm.confirmNewPassword} onChange={e => setProfileForm({ ...profileForm, confirmNewPassword: e.target.value })} />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <div className="d-flex justify-content-end">
                                            <Button type="submit" variant="primary" className="px-4 py-2">Update Profile</Button>
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        )
                    }
                </div >

                {/* INVOICES VIEW - TABLE FORMAT (Matches Clinical Admin) */}
                {
                    view === 'invoices' && (
                        <div className="card h-100 p-0 radius-12 fade-in-up border-0 shadow-sm" style={{ marginLeft: '10px', marginRight: '10px' }}>
                            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3">
                                <div className="d-flex align-items-center gap-3">
                                    <div className="w-40-px h-40-px radius-8 bg-primary-50 text-primary-600 d-flex justify-content-center align-items-center text-xl shadow-sm">
                                        <Icon icon="solar:bill-list-bold" />
                                    </div>
                                    <div>
                                        <h5 className="mb-0 fw-bold text-dark">Consultation Invoices</h5>
                                        <p className="text-sm text-secondary mb-0">Billing history for your patient consultations</p>
                                    </div>
                                </div>
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <div className="search-box">
                                        <input
                                            type="text"
                                            className="form-control form-control-sm w-auto ps-40 h-40-px radius-8"
                                            placeholder="Name or Invoice #"
                                            value={invoiceSearch}
                                            onChange={(e) => setInvoiceSearch(e.target.value)}
                                            style={{ minWidth: '220px' }}
                                        />
                                        <Icon icon="lucide:search" className="position-absolute top-50 start-0 translate-middle-y ms-20 text-secondary-light text-lg" />
                                    </div>
                                    <input
                                        type="date"
                                        className="form-control form-control-sm h-40-px radius-8"
                                        value={invoiceDateFilter}
                                        onChange={(e) => setInvoiceDateFilter(e.target.value)}
                                        style={{ width: '160px' }}
                                    />
                                </div>
                            </div>

                            <div className="card-body p-0">
                                {loading ? (
                                    <div className="p-5 text-center">
                                        <div className="spinner-border text-primary" role="status"></div>
                                        <p className="mt-3 text-secondary">Loading invoices...</p>
                                    </div>
                                ) : invoices.length === 0 ? (
                                    <div className="p-5 text-center text-secondary">
                                        <div className="d-flex flex-column align-items-center opacity-50">
                                            <Icon icon="solar:bill-check-linear" className="display-4 mb-2" />
                                            <p className="mb-0">No consultation invoices found.</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table bordered-table mb-0 align-middle table-hover">
                                            <thead className="bg-light border-bottom">
                                                <tr>
                                                    <th className="ps-4 py-3 text-secondary text-xs fw-bold text-uppercase">Invoice #</th>
                                                    <th className="py-3 text-secondary text-xs fw-bold text-uppercase">Patient Details</th>
                                                    <th className="py-3 text-secondary text-xs fw-bold text-uppercase">Date</th>
                                                    <th className="py-3 text-secondary text-xs fw-bold text-uppercase">Amount</th>
                                                    <th className="py-3 text-secondary text-xs fw-bold text-uppercase">Status</th>
                                                    <th className="py-3 text-end pe-4 text-secondary text-xs fw-bold text-uppercase">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {(() => {
                                                    const searchLower = invoiceSearch.toLowerCase();
                                                    const filtered = invoices.filter(inv => {
                                                        const patientName = (inv.patientId?.name || inv.customerInfo?.name || 'Guest Patient').toLowerCase();
                                                        const patientPhone = (inv.patientId?.phone || inv.customerInfo?.phone || '').toLowerCase();
                                                        const invoiceNum = (inv.invoiceNumber || inv._id.slice(-6).toUpperCase()).toLowerCase();

                                                        const matchesSearch = patientName.includes(searchLower) ||
                                                            patientPhone.includes(searchLower) ||
                                                            invoiceNum.includes(searchLower);

                                                        let matchesDate = true;
                                                        if (invoiceDateFilter) {
                                                            const invDate = new Date(inv.date || inv.createdAt).toISOString().split('T')[0];
                                                            matchesDate = invDate === invoiceDateFilter;
                                                        }

                                                        return matchesSearch && matchesDate;
                                                    });

                                                    if (filtered.length === 0) {
                                                        return (
                                                            <tr>
                                                                <td colSpan="6" className="text-center p-5 text-secondary">
                                                                    No invoices match your search criteria.
                                                                </td>
                                                            </tr>
                                                        );
                                                    }

                                                    return filtered.map(inv => (
                                                        <tr key={inv._id}>
                                                            <td className="ps-4 fw-bold text-primary text-sm">#{inv.invoiceNumber || inv._id.slice(-6).toUpperCase()}</td>
                                                            <td>
                                                                <div className="d-flex flex-column">
                                                                    <span className="fw-bold text-dark">{inv.patientId?.name || inv.customerInfo?.name || 'Guest Patient'}</span>
                                                                    <span className="text-xs text-secondary">{inv.patientId?.phone || inv.customerInfo?.phone || 'No Phone'}</span>
                                                                </div>
                                                            </td>
                                                            <td className="text-secondary text-sm">
                                                                {new Date(inv.date || inv.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                                                            </td>
                                                            <td className="fw-bold text-dark">₹{Number(inv.amount).toLocaleString()}</td>
                                                            <td>
                                                                <span className={`px-2 py-1 radius-4 text-xs fw-bold ${inv.status === 'Paid' ? 'bg-success-focus text-success-600' : 'bg-warning-focus text-warning-600'}`}>
                                                                    {inv.status.toUpperCase()}
                                                                </span>
                                                            </td>
                                                            <td className="text-end pe-4">
                                                                <div className="d-flex justify-content-end gap-2">
                                                                    <button
                                                                        onClick={() => { setSelectedInvoice(inv); setShowInvoiceModal(true); }}
                                                                        className="btn btn-sm btn-light text-primary-600 hover-bg-primary-50 fw-bold d-flex align-items-center gap-1 radius-6 px-2"
                                                                        title="View / Download"
                                                                    >
                                                                        <Icon icon="solar:eye-bold" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleEmailInvoice(inv._id)}
                                                                        className="btn btn-sm btn-light text-info-600 hover-bg-info-50 fw-bold d-flex align-items-center gap-1 radius-6 px-2"
                                                                        title="Email Invoice"
                                                                    >
                                                                        <Icon icon="solar:letter-bold" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ));
                                                })()}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )
                }
            </main >

            {/* PRESCRIPTION MODAL */}
            < Modal show={showRxModal} onHide={() => setShowRxModal(false)} size="lg" centered >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">
                        {currentAppt?.readOnly ? 'Prescription Details (Read Only)' : `Prescribe: ${currentAppt?.patientId?.name || currentAppt?.guestName}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Tab.Container activeKey={rxTab} onSelect={(k) => setRxTab(k)}>
                        <Nav variant="tabs" className="mb-3">
                            <Nav.Item><Nav.Link eventKey="write">Write Prescription</Nav.Link></Nav.Item>
                            <Nav.Item><Nav.Link eventKey="upload">Upload Image</Nav.Link></Nav.Item>
                            {assistantAnalysis && (
                                <Nav.Item>
                                    <Nav.Link eventKey="assistant" className="d-flex align-items-center gap-2">
                                        <Badge bg="info" pill>AI</Badge> Assistant Analysis
                                    </Nav.Link>
                                </Nav.Item>
                            )}
                        </Nav>
                        <Tab.Content>
                            <Tab.Pane eventKey="write">
                                <Form.Label>Medicines</Form.Label>
                                {rxForm.medicines.map((med, idx) => (
                                    <Row key={idx} className="mb-2 g-2">
                                        <Col md={4}><Form.Control placeholder="Medicine Name" value={med.name} onChange={e => updateMedicine(idx, 'name', e.target.value)} disabled={currentAppt?.readOnly} /></Col>
                                        <Col md={3}><Form.Control placeholder="Dosage (e.g. 500mg)" value={med.dosage} onChange={e => updateMedicine(idx, 'dosage', e.target.value)} disabled={currentAppt?.readOnly} /></Col>
                                        <Col md={3}><Form.Control placeholder="Freq (1-0-1)" value={med.frequency} onChange={e => updateMedicine(idx, 'frequency', e.target.value)} disabled={currentAppt?.readOnly} /></Col>
                                        <Col md={1}>
                                            {!currentAppt?.readOnly && <Button variant="danger" size="sm" onClick={() => removeMedicine(idx)}><Icon icon="solar:trash-bin-trash-bold" /></Button>}
                                        </Col>
                                    </Row>
                                ))}
                                {!currentAppt?.readOnly && <Button variant="light text-primary" size="sm" onClick={addMedicine} className="mb-3">+ Add Medicine</Button>}

                                <Form.Group>
                                    <Form.Label>Clinical Notes / Advice</Form.Label>
                                    <Form.Control as="textarea" rows={3} value={rxForm.notes} onChange={e => setRxForm({ ...rxForm, notes: e.target.value })} disabled={currentAppt?.readOnly} />
                                </Form.Group>
                            </Tab.Pane>
                            <Tab.Pane eventKey="upload">
                                <div className="text-center p-5 border border-dashed rounded bg-light">
                                    <Icon icon="solar:upload-track-bold-duotone" className="text-4xl text-secondary mb-3" />
                                    <p>Upload a photo of handwritten prescription</p>
                                    {!currentAppt?.readOnly && <Form.Control type="file" onChange={handleImageUpload} accept="image/*" />}
                                    {rxForm.image && <img src={rxForm.image} alt="Preview" className="mt-3 img-fluid rounded" style={{ maxHeight: '200px' }} />}
                                </div>
                            </Tab.Pane>
                            <Tab.Pane eventKey="assistant">
                                {assistantAnalysis ? (
                                    <div className="bg-light p-4 rounded-4 border">
                                        <div className="d-flex align-items-center gap-3 mb-4">
                                            <div>
                                                <h6 className="mb-0 fw-bold">Observations by Assistant</h6>
                                                <small className="text-muted">Recorded by {assistantAnalysis.assistantId?.name}</small>
                                            </div>
                                        </div>

                                        <Card className="border-0 shadow-sm mb-4">
                                            <Card.Body>
                                                {/* VITALS SECTION */}
                                                {assistantAnalysis.vitals && (
                                                    <div className="mb-4 pb-3 border-bottom">
                                                        <h6 className="text-xs text-uppercase text-secondary fw-bold mb-3">Patient Vitals</h6>
                                                        <div className="d-flex flex-wrap gap-4">
                                                            {assistantAnalysis.vitals.bp && <div><span className="text-secondary text-xs d-block text-uppercase">BP</span><span className="fw-bold fs-5 text-dark">{assistantAnalysis.vitals.bp}</span></div>}
                                                            {assistantAnalysis.vitals.temperature && <div><span className="text-secondary text-xs d-block text-uppercase">Temp</span><span className="fw-bold fs-5 text-dark">{assistantAnalysis.vitals.temperature}</span></div>}
                                                            {assistantAnalysis.vitals.pulse && <div><span className="text-secondary text-xs d-block text-uppercase">Pulse</span><span className="fw-bold fs-5 text-dark">{assistantAnalysis.vitals.pulse} <small className="fs-6 text-muted">bpm</small></span></div>}
                                                            {assistantAnalysis.vitals.spo2 && <div><span className="text-secondary text-xs d-block text-uppercase">SpO2</span><span className="fw-bold fs-5 text-dark">{assistantAnalysis.vitals.spo2} <small className="fs-6 text-muted">%</small></span></div>}
                                                            {assistantAnalysis.vitals.weight && <div><span className="text-secondary text-xs d-block text-uppercase">Weight</span><span className="fw-bold fs-5 text-dark">{assistantAnalysis.vitals.weight} <small className="fs-6 text-muted">kg</small></span></div>}
                                                        </div>
                                                        {assistantAnalysis.symptoms && assistantAnalysis.symptoms.length > 0 && (
                                                            <div className="mt-3 pt-2">
                                                                <span className="text-secondary text-xs text-uppercase">Symptoms: </span>
                                                                <span className="fw-medium text-dark">{assistantAnalysis.symptoms.join(', ')}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                <h6 className="text-xs text-uppercase text-secondary fw-bold mb-2">Internal Notes</h6>
                                                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{assistantAnalysis.notes || 'No specific observations recorded.'}</p>
                                            </Card.Body>
                                        </Card>

                                        {/* Display Multiple Reports */}
                                        {assistantAnalysis.reports && assistantAnalysis.reports.length > 0 ? (
                                            <div>
                                                <h6 className="text-xs text-uppercase text-secondary fw-bold mb-3">Previous Reports / Files</h6>
                                                <Table hover responsive className="bg-white rounded-4 overflow-hidden shadow-sm align-middle">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th className="ps-4">File Name</th>
                                                            <th>Type</th>
                                                            <th className="text-end pe-4">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {assistantAnalysis.reports.map((report, idx) => (
                                                            <tr key={idx}>
                                                                <td className="ps-4 fw-medium">{report.name || `Report ${idx + 1}`}</td>
                                                                <td>
                                                                    <Badge bg="light" text="dark" className="border">
                                                                        {report.type?.split('/')[1]?.toUpperCase() || 'FILE'}
                                                                    </Badge>
                                                                </td>
                                                                <td className="text-end pe-4">
                                                                    <Button variant="primary" size="sm" onClick={() => handleViewImage(report.url)}>
                                                                        <Icon icon="solar:eye-bold" /> View
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : assistantAnalysis.prescriptionUrl ? (
                                            // Backward Compatibility for single Rx
                                            <div>
                                                <h6 className="text-xs text-uppercase text-secondary fw-bold mb-3">Patient Uploaded Rx / Previous File</h6>
                                                <div className="bg-white p-3 rounded-4 border text-center shadow-sm">
                                                    <img
                                                        src={assistantAnalysis.prescriptionUrl}
                                                        alt="Assistant Upload"
                                                        className="img-fluid rounded-3"
                                                        style={{ maxHeight: '500px', cursor: 'pointer' }}
                                                        onClick={() => handleViewImage(assistantAnalysis.prescriptionUrl)}
                                                    />
                                                    <div className="mt-3">
                                                        <Button variant="outline-primary" size="sm" onClick={() => handleViewImage(assistantAnalysis.prescriptionUrl)}>
                                                            <Icon icon="solar:magnifer-zoom-in-bold" /> Open Full Screen
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="text-muted text-center py-4">No files attached.</p>
                                        )}

                                    </div>
                                ) : (
                                    <div className="text-center p-5 text-secondary">
                                        <Icon icon="solar:info-circle-bold" className="display-4 opacity-25 mb-3" />
                                        <p>No assistant analysis available for this appointment.</p>
                                    </div>
                                )}
                            </Tab.Pane>
                        </Tab.Content>
                    </Tab.Container>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => window.print()} className="me-auto"><Icon icon="solar:printer-bold" className="me-2" /> Print</Button>
                    <Button variant="secondary" onClick={() => setShowRxModal(false)}>Close</Button>
                    {!currentAppt?.readOnly && <Button variant="primary" onClick={submitPrescription}>Complete & Send</Button>}
                </Modal.Footer>
            </Modal >
            {/* PATIENT HISTORY MODAL */}
            < Modal show={showPatientModal} onHide={() => setShowPatientModal(false)} size="lg" centered >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Patient History: {selectedPatient?.info?.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedPatient && (
                        <>
                            <Row className="mb-4">
                                <Col md={4}>
                                    <div className="text-secondary text-xs text-uppercase fw-bold">Contact</div>
                                    <div>{selectedPatient.info.phone}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="text-secondary text-xs text-uppercase fw-bold">Email</div>
                                    <div>{selectedPatient.info.email}</div>
                                </Col>
                                <Col md={4}>
                                    <div className="text-secondary text-xs text-uppercase fw-bold">Last Visit</div>
                                    <div>{selectedPatient.info.lastVisit ? new Date(selectedPatient.info.lastVisit).toLocaleDateString() : '-'}</div>
                                </Col>
                            </Row>

                            <h6 className="fw-bold mb-3 border-bottom pb-2">Appointment History</h6>
                            {selectedPatient.history.length === 0 ? (
                                <p className="text-secondary">No recorded history.</p>
                            ) : (
                                <Table hover size="sm" className="align-middle">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Date</th>
                                            <th>Type</th>
                                            <th>Status</th>
                                            <th>Prescription</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedPatient.history.map(app => (
                                            <tr key={app._id}>
                                                <td>{new Date(app.date).toLocaleDateString()} <span className="text-secondary text-xs ms-1">{app.timeSlot}</span></td>
                                                <td>{app.type}</td>
                                                <td><Badge bg={app.status === 'Completed' ? 'success' : app.status === 'Active' ? 'primary' : 'secondary'}>{app.status}</Badge></td>
                                                <td>
                                                    {app.status === 'Completed' ? (
                                                        <Button variant="link" className="p-0 text-decoration-none" onClick={() => handleViewRx(app)}>
                                                            <Icon icon="solar:eye-bold" /> View Rx
                                                        </Button>
                                                    ) : '-'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPatientModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal >

            {/* INVOICE DETAIL MODAL */}
            {
                selectedInvoice && (
                    <InvoiceDetailModal
                        show={showInvoiceModal}
                        onHide={() => setShowInvoiceModal(false)}
                        invoice={selectedInvoice}
                        onEmail={handleEmailInvoice}
                    />
                )
            }
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .modal.show { visibility: visible; position: absolute; left: 0; top: 0; width: 100%; margin: 0; }
                    .modal.show .modal-dialog { width: 100%; max-width: 100%; margin: 0; }
                    .modal.show .modal-content { border: none; box-shadow: none; }
                    .modal.show .modal-body { padding: 20px; }
                    .modal.show .modal-body * { visibility: visible; }
                    .modal-footer, .btn-close, .modal-header { display: none !important; }
                }
            `}</style>
        </section >
    );
};

export default DoctorDashboard;
